import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react'

type ToastKind = 'success' | 'error' | 'warn' | 'info'
type ToastItem = { id: number; kind: ToastKind; message: string }
type ToastApi = {
  success: (m: string) => void
  error: (m: string) => void
  warn: (m: string) => void
  info: (m: string) => void
}

const Ctx = createContext<ToastApi | null>(null)

/** App-wide toasts. Wrap the app once; call `useToast()` anywhere for success/error feedback. */
export function useToast(): ToastApi {
  const c = useContext(Ctx)
  if (!c) throw new Error('useToast must be used within <ToastProvider>')
  return c
}

let seq = 0
const ICONS = { success: CheckCircle2, error: XCircle, warn: AlertTriangle, info: Info }

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const remove = useCallback((id: number) => setItems(t => t.filter(x => x.id !== id)), [])
  const push = useCallback((kind: ToastKind, message: string) => {
    const id = ++seq
    setItems(t => [...t, { id, kind, message }])
    setTimeout(() => remove(id), kind === 'error' ? 7000 : 4500)
  }, [remove])

  const api: ToastApi = {
    success: m => push('success', m),
    error: m => push('error', m),
    warn: m => push('warn', m),
    info: m => push('info', m),
  }

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toast-wrap" aria-live="polite">
        {items.map(t => {
          const Icon = ICONS[t.kind]
          return (
            <div key={t.id} className={`toast toast-${t.kind}`} role="status">
              <Icon size={18} className="toast-ico" />
              <span className="toast-msg">{t.message}</span>
              <button className="toast-x" onClick={() => remove(t.id)} aria-label="Dismiss"><X size={14} /></button>
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}
